import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { toast } from "react-toastify";
import { VehicleLoadScanAgent } from "../../app/api/vehicleLoadScan/VehicleLoadScanAgent";
import styles from "../rtsScanModule/RtsScanModule.styles";
import ManualEntryComponent from "./ManualEntryComponent";
import LoadScanPackages from "./LoadScanPackages";
import { ReceivingPackageResponse } from "../../app/models/responseModels/ReceivingPkgResponse";
import { LoadScanningRequest } from "../../app/models/requestModels/loadScanningRequest";
import VideoCapture from "../../components/common/VideoCapture/VideoCapture";
import getAxiosError from "../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { useAppDispatch } from "../../app/features/store/store";
import InfoModal from "../../components/common/modalPopup/InfoModal";
import Loader from "../../components/common/loader/Loader";

const breadcrumb = ["Vehicle Load Scan"];

interface location {
  locationName: string;
  locationId: string;
  isStore: boolean;
  isParcelCenter: boolean;
}

const VehicleLoadScan = () => {
  const dispatch = useAppDispatch();
  const [availableStoresAndPcs, setAvailableStoresAndPcs] = useState<
    location[]
  >([]);
  const [selectedStoreOrPc, setSelectedStoreOrPc] = useState<location>();
  const [userLocation, setUserLocation] = useState<any>({});
  const [isMobile, setIsMobile] = useState(false);
  const [isManualEntryOpen, SetManualEntry] = useState(false);
  const [clientEmailIds, setClientEmailIds] = useState<string[]>();
  const [selectedClientEmailId, setSelectedClientEmailId] = useState("");
  const [latestScannedSid, setLatestScannedSid] = useState<string | null>("");
  const [latestScannedId, setLatestScannedId] = useState<string | null>("");
  const [openSidModal, setOpenSidModal] = useState(false);
  const [isLivLoading, setLivLoading] = useState(false);
  const [receivingPkgs, setReceivingPkgs] = useState<
    ReceivingPackageResponse[]
  >([]);
  const [alreadyScannedPkgs, setAlreadyScannedPkgs] = useState<
    ReceivingPackageResponse[]
  >([]);
  const [confirmedPkgs, setConfirmedPkgs] = useState<
    ReceivingPackageResponse[]
  >([]);

  const findAvailableStoreAndPcsByLocation = useCallback(async () => {
    setLivLoading(true);
    try {
      const position: any = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
      const { longitude, latitude } = position.coords;
      setUserLocation({ latitude, longitude });
      const geofenceResponse = await VehicleLoadScanAgent.checkGeofence({
        latitude: latitude,
        longitude: longitude,
      });
      if (
        geofenceResponse.status === 200 &&
        geofenceResponse.data.body.length > 0
      ) {
        setAvailableStoresAndPcs(geofenceResponse.data.body);
        setSelectedStoreOrPc(geofenceResponse.data.body[0]);
      }
    } catch (error: any) {
      toast.error(error.message);
      console.error("Error checking location details:", error);
    }
    setLivLoading(false);
  }, []);

  const fetchDriverEmailsByStore = useCallback(
    async (storeId: string) => {
      const { longitude, latitude } = userLocation;
      if (longitude && latitude) {
        try {
          const driverEmailRes =
            await VehicleLoadScanAgent.fetchDriverEmailsByStore(
              storeId,
              longitude,
              latitude
            );
          if (driverEmailRes.status === 200) {
            setClientEmailIds(driverEmailRes.data);
          }
        } catch (error: any) {
          toast.error(error.message);
          console.error("Error fetching driver email Ids:", error);
        }
      }
    },
    [userLocation]
  );

  const fetchReceivingsByStoreAndDriver = useCallback(
    async (storeId: string, driverEmailId: string) => {
      const { longitude, latitude } = userLocation;
      if (longitude && latitude) {
        try {
          const fetchedReceivingsRes =
            await VehicleLoadScanAgent.fetchRecevingsForCurrentDay(
              driverEmailId,
              storeId,
              longitude,
              latitude
            );
          const { status, data } = fetchedReceivingsRes;
          if (status === 200 && data && Array.isArray(data)) {
            const sanitizedData = data.map(sanitizeReceivingPackage);
            setReceivingPkgs(sanitizedData as ReceivingPackageResponse[]);
            setConfirmedPkgs([]);
            setLatestScannedId("");
            setLatestScannedSid("");
            setAlreadyScannedPkgs(
              sanitizedData.filter((pkg) => pkg.isLoadScanned)
            );
          }
        } catch (error: any) {
          const appError = getAxiosError(error);
          toast.error("Error fetching receiving packages from DB");
          console.error(
            "Error fetching receiving packages for store and driver emailId:",
            error
          );
        }
      }
    },
    [userLocation]
  );

  const sanitizeReceivingPackage = (data: any): ReceivingPackageResponse => {
    return {
      driverUserId: data.driverUserId,
      toteId: data.toteId,
      dropNumber: data.dropNumber,
      sid: data.sid,
      orderId: data.orderId,
      poNumber: data.poNumber,
      storeNumber: data.storeNumber,
      displayTripId: data.displayTripId,
      storeId: data.storeId,
      customerAddressLine1: data.customerAddressLine1,
      customerCity: data.customerCity,
      customerState: data.customerState,
      customerPostalcode: data.customerPostalcode,
      isLoadScanned: data.isLoadScanned as boolean,
    };
  };

  useEffect(() => {
    findAvailableStoreAndPcsByLocation();
    const mediaQuery = window.matchMedia("(max-width: 824px)");
    setIsMobile(mediaQuery.matches);
    const handleResize = () => {
      setIsMobile(mediaQuery.matches);
    };
    mediaQuery.addListener(handleResize);
    return () => {
      mediaQuery.removeListener(handleResize);
    };
  }, []);

  useEffect(() => {
    setReceivingPkgs([]);
    setConfirmedPkgs([]);
    setLatestScannedId("");
    setLatestScannedSid("");
    setSelectedClientEmailId("");
    setAlreadyScannedPkgs([]);
    if (selectedStoreOrPc?.isStore) {
      fetchDriverEmailsByStore(selectedStoreOrPc?.locationId as string);
    }
  }, [selectedStoreOrPc]);

  useEffect(() => {
    if (selectedClientEmailId) {
      fetchReceivingsByStoreAndDriver(
        selectedStoreOrPc?.locationId as string,
        selectedClientEmailId
      );
    }
  }, [selectedClientEmailId]);

  useEffect(() => {
    if (openSidModal) {
      const timer = setTimeout(() => {
        handleSidInfoModalClose(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [openSidModal]);

  const handleLocationChange = (e: any) => {
    const locationId = e.target.value;
    setSelectedStoreOrPc(
      availableStoresAndPcs.find((e) => locationId === e.locationId) as location
    );
  };

  const handleSidInfoModalClose = (value: boolean) => {
    setOpenSidModal(value);
  };

  const handleCapturedBarcode = (barcode: string) => {
    setReceivingPkgs((prevReceivingPkgs) => {
      const updatedReceivingPkgs = prevReceivingPkgs.filter(
        (pkg) => !pkg.isLoadScanned && !(pkg.toteId === barcode)
      );
      const targatedPkg = prevReceivingPkgs.find(
        (pkg) => !pkg.isLoadScanned && pkg.toteId === barcode
      );
      let alreadyScannedPkg: ReceivingPackageResponse | null | undefined = null;
      setAlreadyScannedPkgs((prev) => {
        alreadyScannedPkg = prev.find((pkg) => pkg.toteId === barcode);
        return prev;
      });
      let foundInConfirmedPkgs = false;
      setConfirmedPkgs((prevConfirmedPkgs) => {
        const alreadyConfirmedPkg = prevConfirmedPkgs.find(
          (pkg) => pkg.toteId === barcode
        );
        if (alreadyConfirmedPkg) {
          foundInConfirmedPkgs = true;
          toast.warn("Package Already Confirmed!");
          setLatestScannedSid(alreadyConfirmedPkg.sid.toUpperCase());
          setLatestScannedId(alreadyConfirmedPkg.toteId);
          setOpenSidModal(true);
        } else if (alreadyScannedPkg) {
          toast.warn("Package Already Scanned!");
          setLatestScannedSid(alreadyScannedPkg.sid.toUpperCase());
          setLatestScannedId(alreadyScannedPkg.toteId);
          setOpenSidModal(true);
        } else if (targatedPkg) {
          toast.info("Package confirmed to be submitted!");
          setLatestScannedSid(targatedPkg.sid.toUpperCase());
          setLatestScannedId(targatedPkg.toteId);
          setOpenSidModal(true);
          return [...prevConfirmedPkgs, targatedPkg];
        }
        return prevConfirmedPkgs;
      });

      if (!targatedPkg && !alreadyScannedPkg && !foundInConfirmedPkgs) {
        toast.warn("Package Not Available!");
      }
      return updatedReceivingPkgs;
    });
  };

  const handlePackageSubmit = async () => {
    const pkgsToteIds = confirmedPkgs.map((pkg) => pkg.toteId);

    const livRequest: LoadScanningRequest = {
      toteIds: pkgsToteIds,
      clientEmailId: selectedClientEmailId,
      clientStoreId: selectedStoreOrPc?.locationId as string,
    };
    try {
      const livScannedRes = await VehicleLoadScanAgent.updateRecevings(
        livRequest
      );
      const { status } = livScannedRes;
      if (status === 200) {
        fetchReceivingsByStoreAndDriver(
          selectedStoreOrPc?.locationId as string,
          selectedClientEmailId
        );
        toast.success("Packages scanned successfully.");
      }
    } catch (error: any) {
      toast.error("Couldn't update packages as scanned!");
      console.error("Error updating receiving packages as scanned:", error);
    }
  };

  const renderLatestSidInfo = () => {
    return (
      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        <Typography>
          <b>ID:</b> {latestScannedId}
        </Typography>
        <Typography>
          <b>SID:</b> {latestScannedSid}
        </Typography>
      </Box>
    );
  };

  if (isLivLoading) {
    return (
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: "100vh" }}
      >
        <Loader isLoading={isLivLoading} />
      </Grid>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      {availableStoresAndPcs.length === 0 && userLocation?.latitude && (
        <p>
          Latitude : {userLocation?.latitude}, Logitude:{" "}
          {userLocation?.longitude} Not in Geofence Area
        </p>
      )}
      {availableStoresAndPcs.length !== 0 && (
        <>
          {!isManualEntryOpen && (
            <Box>
              {selectedClientEmailId && receivingPkgs.length >= 0 && (
                <>
                  <VideoCapture setLatestScanBarcode={handleCapturedBarcode} />
                  {latestScannedSid && renderLatestSidInfo()}
                  {latestScannedSid && (
                    <InfoModal
                      open={openSidModal}
                      title={"SID"}
                      onClose={handleSidInfoModalClose}
                      textInfo={latestScannedSid}
                    />
                  )}
                </>
              )}
              <Grid container spacing={2}>
                <Grid
                  item
                  sx={{ display: "flex", justifyContent: "end", mt: 2 }}
                  xs={6}
                  sm={6}
                  md={6}
                  lg={6}
                >
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label={
                      selectedStoreOrPc?.isStore ? "Store" : "ParcelCenter"
                    }
                    name={selectedStoreOrPc?.isStore ? "Store" : "ParcelCenter"}
                    value={selectedStoreOrPc?.locationId}
                    onChange={(e) => handleLocationChange(e)}
                  >
                    {availableStoresAndPcs.map((location) => (
                      <MenuItem
                        key={location.locationId}
                        value={location.locationId}
                      >
                        {location.locationName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid
                  item
                  sx={{ display: "flex", justifyContent: "end", mt: 2 }}
                  xs={6}
                  sm={6}
                  md={6}
                  lg={6}
                >
                  {selectedStoreOrPc?.isStore && (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Client Email Id"
                      name="clientEmailId"
                      value={selectedClientEmailId}
                      onChange={(e) => setSelectedClientEmailId(e.target.value)}
                    >
                      {clientEmailIds &&
                        clientEmailIds.map((email, index) => (
                          <MenuItem key={index} value={email}>
                            {email}
                          </MenuItem>
                        ))}
                      {!clientEmailIds && (
                        <MenuItem value="" key="no-email">
                          No Email Found
                        </MenuItem>
                      )}
                    </TextField>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
          {isManualEntryOpen && (
            <>
              <ManualEntryComponent
                onClose={() => SetManualEntry(false)}
                onSubmit={handleCapturedBarcode}
              />
              {latestScannedSid && renderLatestSidInfo()}
              {latestScannedSid && (
                <InfoModal
                  open={openSidModal}
                  title={"SID"}
                  onClose={handleSidInfoModalClose}
                  textInfo={latestScannedSid}
                />
              )}
            </>
          )}
          {selectedClientEmailId && (
            <LoadScanPackages
              receivingPkgs={receivingPkgs}
              confirmedPkgs={confirmedPkgs}
              alreadyScannedPkgs={alreadyScannedPkgs}
            />
          )}
          <Divider sx={{ mt: 1, mb: 1 }} />
          {selectedClientEmailId && (
            <Box
              sx={{
                position: "fixed",
                bottom: 1,
                left: 1,
                padding: 1,
                width: "100%",
                backgroundColor: "white",
                zIndex: 1000, // Ensure it appears above other content
              }}
            >
              <Box style={styles.scanModuleButtons}>
                <Button
                  style={{
                    ...styles.buttonItem,
                    visibility: !isManualEntryOpen ? "visible" : "hidden",
                  }}
                  variant="outlined"
                  onClick={() => SetManualEntry(true)}
                >
                  Enter Manually
                </Button>
                <Button
                  disabled={confirmedPkgs.length === 0}
                  style={styles.buttonItem}
                  onClick={handlePackageSubmit}
                  variant="contained"
                >
                  Submit
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default VehicleLoadScan;
